import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import {
  makeStyles,
  Paper,
  Typography,
  LinearProgress,
  IconButton,
  Button,
  Tooltip,
  Chip,
  Box,
} from "@material-ui/core";
import {
  PlayArrow,
  Pause,
  Stop,
  Close,
  Minimize,
  Fullscreen,
  VolumeUp,
  AttachFile,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../Auth/AuthContext";

const BroadcastContext = createContext();

const useStyles = makeStyles((theme) => ({
  floatingPill: {
    position: "fixed",
    bottom: 24,
    right: 24,
    zIndex: 1400,
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
    color: "#fff",
    border: "1px solid rgba(56, 189, 248, 0.4)",
    borderRadius: 30,
    padding: "8px 16px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)",
    cursor: "pointer",
    transition: "all 0.25s ease-in-out",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 15px 30px -5px rgba(56, 189, 248, 0.3)",
      borderColor: "#38bdf8",
    },
  },
  pulseDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: "#10b981",
    boxShadow: "0 0 10px #10b981",
    animation: "$pulse 1.5s infinite",
  },
  pulseDotPaused: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: "#f59e0b",
    boxShadow: "0 0 10px #f59e0b",
  },
  pulseDotFinished: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: "#38bdf8",
  },
  "@keyframes pulse": {
    "0%": { transform: "scale(0.95)", opacity: 0.8 },
    "50%": { transform: "scale(1.3)", opacity: 1 },
    "100%": { transform: "scale(0.95)", opacity: 0.8 },
  },
  floatingWindow: {
    position: "fixed",
    bottom: 24,
    right: 24,
    width: 440,
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "85vh",
    zIndex: 1400,
    background: "#0f172a",
    color: "#f8fafc",
    borderRadius: 16,
    border: "1px solid #334155",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.6)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    animation: "$slideUp 0.25s ease-out",
  },
  "@keyframes slideUp": {
    from: { opacity: 0, transform: "translateY(20px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  windowHeader: {
    padding: "12px 16px",
    background: "#1e293b",
    borderBottom: "1px solid #334155",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  windowBody: {
    padding: 16,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: "#334155",
    "& .MuiLinearProgress-bar": {
      background: "linear-gradient(90deg, #38bdf8, #818cf8)",
      borderRadius: 4,
    },
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 8,
    textAlign: "center",
  },
  statCard: {
    padding: "8px 6px",
    borderRadius: 8,
    background: "#1e293b",
    border: "1px solid #334155",
  },
  terminalBox: {
    backgroundColor: "#020617",
    border: "1px solid #1e293b",
    borderRadius: 8,
    padding: 10,
    fontFamily: "monospace",
    fontSize: "0.78rem",
    height: 160,
    overflowY: "auto",
    color: "#cbd5e1",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  actionsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    paddingTop: 4,
  },
}));

export const BroadcastProvider = ({ children }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0, current: 0 });
  const [currentContact, setCurrentContact] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [logs, setLogs] = useState([]);
  const [isFloatingOpen, setIsFloatingOpen] = useState(false);
  const [isFloatingVisible, setIsFloatingVisible] = useState(false);

  const cancelRef = useRef(false);
  const pauseRef = useRef(false);
  const countdownIntervalRef = useRef(null);
  const terminalEndRef = useRef(null);

  const addLog = useCallback((text, color = "#cbd5e1") => {
    const timestamp = new Date().toLocaleTimeString("pt-BR");
    setLogs((prev) => [...prev, { text, color, timestamp }]);
  }, []);

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  useEffect(() => {
    if (terminalEndRef.current) {
      terminalEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs]);

  // Função auxiliar para resolver ou criar o atendimento
  const resolveTicketForRecipient = useCallback(async (contact) => {
    if (contact.ticketId) {
      return contact.ticketId;
    }

    const raw = String(contact.number).replace(/\D/g, "");
    const numBR = (raw.length === 10 || raw.length === 11) ? `55${raw}` : raw;

    let numWithout9 = "";
    if (numBR.startsWith("55") && numBR.length === 13) {
      const ddd = numBR.slice(2, 4);
      numWithout9 = `55${ddd}${numBR.slice(5)}`;
    } else if (numBR.startsWith("55") && numBR.length === 12) {
      const ddd = numBR.slice(2, 4);
      numWithout9 = `55${ddd}9${numBR.slice(4)}`;
    }

    const candidateNumbers = [numBR, raw, numWithout9].filter(Boolean);

    // 1. Localiza ou cadastra o contato via /contact
    let contactId = contact.id;
    if (!contactId) {
      for (const num of candidateNumbers) {
        try {
          const { data } = await api.post("/contact", {
            name: contact.name || num,
            number: num,
          });
          if (data?.id) {
            contactId = data.id;
            break;
          }
        } catch (e) {}
      }
    }

    if (!contactId) {
      for (const num of candidateNumbers) {
        try {
          const { data } = await api.get("/contacts", {
            params: { searchParam: num },
          });
          if (data.contacts?.[0]?.id) {
            contactId = data.contacts[0].id;
            break;
          }
        } catch (err) {}
      }
    }

    if (!contactId) {
      throw new Error(`Não foi possível localizar ou cadastrar o contato ${contact.number}`);
    }

    // 2. Tenta criar o atendimento (se já houver aberto, backend retorna 200)
    try {
      const { data: ticket } = await api.post("/tickets", {
        contactId: contactId,
        userId: user?.id,
        status: "open",
      });
      if (ticket?.id) {
        return ticket.id;
      }
    } catch (err) {
      // Fallback: busca qualquer ticket existente desse contato
      for (const num of candidateNumbers) {
        try {
          const { data } = await api.get("/tickets", {
            params: { searchParam: num, pageNumber: 1, showAll: "true" },
          });
          const found = data.tickets?.find((t) => t.contactId === contactId) || data.tickets?.[0];
          if (found?.id) {
            if (found.status === "closed") {
              await api.put(`/tickets/${found.id}`, { status: "open", userId: user?.id }).catch(() => {});
            }
            return found.id;
          }
        } catch (e) {}
      }

      throw new Error(`Erro ao gerar atendimento: ${err.response?.data?.message || err.message}`);
    }
  }, [user]);

  // Contagem regressiva segura
  const sleepWithCountdown = (seconds, reasonText = "Aguardando antes do próximo envio...") => {
    return new Promise((resolve) => {
      let remaining = seconds;
      setCountdown(remaining);
      addLog(`⏳ ${reasonText} (${remaining}s restantes)`, "#fbbf24");

      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

      countdownIntervalRef.current = setInterval(() => {
        if (cancelRef.current) {
          clearInterval(countdownIntervalRef.current);
          setCountdown(0);
          resolve();
          return;
        }

        // Se estiver pausado, apenas não decrementa
        if (pauseRef.current) {
          return;
        }

        remaining -= 1;
        setCountdown(remaining);
        if (remaining <= 0) {
          clearInterval(countdownIntervalRef.current);
          setCountdown(0);
          resolve();
        }
      }, 1000);
    });
  };

  // Disparo principal em loop contínuo
  const startBroadcast = useCallback(
    async ({
      recipients,
      messageText = "",
      mediaFile = null,
      audioFile = null,
      minDelay = 20,
      maxDelay = 45,
      batchSize = 15,
      pauseMinutes = 5,
    }) => {
      if (isRunning) {
        toast.warning("Já existe um disparo em andamento!");
        setIsFloatingOpen(true);
        return;
      }

      if (!recipients || recipients.length === 0) {
        toast.error("Nenhum contato válido para envio.");
        return;
      }

      cancelRef.current = false;
      pauseRef.current = false;
      setIsRunning(true);
      setIsPaused(false);
      setIsFinished(false);
      setIsFloatingVisible(true);
      setIsFloatingOpen(false); // Inicia como pill discreto no canto
      setProgress(0);
      setStats({ total: recipients.length, sent: 0, failed: 0, current: 0 });
      clearLogs();

      addLog(`🚀 INICIANDO DISPARO: ${recipients.length} contatos na fila.`, "#38bdf8");
      toast.info(`Disparo iniciado para ${recipients.length} contatos! Você pode navegar livremente.`);

      let sentCount = 0;
      let failCount = 0;

      for (let i = 0; i < recipients.length; i++) {
        if (cancelRef.current) {
          addLog("🛑 Disparo interrompido pelo usuário.", "#f87171");
          toast.warning("Disparo cancelado!");
          break;
        }

        // Aguarda enquanto estiver pausado
        while (pauseRef.current) {
          await new Promise((r) => setTimeout(r, 1000));
          if (cancelRef.current) break;
        }
        if (cancelRef.current) break;

        const contact = recipients[i];
        setCurrentContact(contact);
        setStats({ total: recipients.length, sent: sentCount, failed: failCount, current: i + 1 });
        setProgress(Math.round(((i) / recipients.length) * 100));

        try {
          // 1. Resolve o ticket
          const ticketId = await resolveTicketForRecipient(contact);

          // 2. Personaliza o texto com variáveis
          let personalizedText = messageText;
          if (contact.name) {
            const firstName = contact.name.trim().split(" ")[0];
            personalizedText = personalizedText
              .replace(/{nome}/gi, firstName)
              .replace(/{name}/gi, firstName);
          }

          // 3. Envio de Mídia (Foto, Vídeo ou Documento)
          if (mediaFile) {
            const formData = new FormData();
            formData.append("medias", mediaFile);
            if (personalizedText) formData.append("body", personalizedText);
            await api.post(`/messages/${ticketId}`, formData);
            addLog(`📎 Mídia entregue para ${contact.number} (${contact.name || "Sem nome"})`, "#34d399");
          }

          // 4. Envio de Áudio (PTT)
          if (audioFile) {
            if (mediaFile) await new Promise((r) => setTimeout(r, 1500));
            const formData = new FormData();
            formData.append("medias", audioFile);
            await api.post(`/messages/${ticketId}`, formData);
            addLog(`🎙️ Áudio PTT entregue para ${contact.number} (${contact.name || "Sem nome"})`, "#34d399");
          }

          // 5. Envio de Texto simples (se não houver mídia)
          if (!mediaFile && !audioFile && personalizedText) {
            await api.post(`/messages/${ticketId}`, {
              body: personalizedText,
            });
            addLog(`✅ Mensagem entregue para ${contact.number} (${contact.name || "Sem nome"})`, "#34d399");
          }

          sentCount += 1;
        } catch (err) {
          failCount += 1;
          const msg = err.response?.data?.message || err.message;
          addLog(`❌ Falha para ${contact.number}: ${msg}`, "#f87171");
        }

        setStats({ total: recipients.length, sent: sentCount, failed: failCount, current: i + 1 });
        setProgress(Math.round(((i + 1) / recipients.length) * 100));

        // Se for o último contato, finaliza
        if (i === recipients.length - 1) {
          break;
        }

        // Pausa de lote (Batch pause) anti-ban
        if (batchSize > 0 && (i + 1) % batchSize === 0) {
          const pauseSecs = Math.max(10, pauseMinutes * 60);
          await sleepWithCountdown(pauseSecs, `Pausa de lote (${i + 1} disparos atingidos)`);
        } else {
          // Delay aleatório humano
          const min = Math.max(5, parseInt(minDelay, 10) || 20);
          const max = Math.max(min, parseInt(maxDelay, 10) || 45);
          const delaySecs = Math.floor(Math.random() * (max - min + 1)) + min;
          await sleepWithCountdown(delaySecs, "Simulando digitação humana...");
        }
      }

      setIsRunning(false);
      setIsPaused(false);
      setIsFinished(true);
      setCurrentContact(null);
      setCountdown(0);
      addLog(`🏁 Finalizado: ${sentCount} enviados, ${failCount} falhas.`, "#38bdf8");
      toast.success(`Disparo concluído: ${sentCount} enviados, ${failCount} falhas!`);
    },
    [isRunning, resolveTicketForRecipient, addLog, clearLogs]
  );

  const stopBroadcast = useCallback(() => {
    cancelRef.current = true;
    setIsRunning(false);
    setIsPaused(false);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    setCountdown(0);
    addLog("🛑 O disparo foi interrompido manualmente.", "#f87171");
    toast.info("Disparo interrompido.");
  }, [addLog]);

  const togglePause = useCallback(() => {
    if (!isRunning) return;
    const nextState = !isPaused;
    pauseRef.current = nextState;
    setIsPaused(nextState);
    if (nextState) {
      addLog("⏸️ Disparo pausado temporariamente.", "#fbbf24");
      toast.info("Disparo pausado.");
    } else {
      addLog("▶️ Disparo retomado.", "#34d399");
      toast.info("Disparo retomado!");
    }
  }, [isRunning, isPaused, addLog]);

  const openMonitor = useCallback(() => {
    setIsFloatingOpen(true);
  }, []);

  const closeMonitor = useCallback(() => {
    setIsFloatingOpen(false);
  }, []);

  const dismissFloating = useCallback(() => {
    if (isRunning) {
      setIsFloatingOpen(false);
    } else {
      setIsFloatingVisible(false);
      setIsFloatingOpen(false);
    }
  }, [isRunning]);

  return (
    <BroadcastContext.Provider
      value={{
        isRunning,
        isPaused,
        isFinished,
        progress,
        stats,
        currentContact,
        countdown,
        logs,
        isFloatingOpen,
        isFloatingVisible,
        startBroadcast,
        stopBroadcast,
        togglePause,
        openMonitor,
        closeMonitor,
        dismissFloating,
        addLog,
        clearLogs,
      }}
    >
      {children}

      {/* 1. PILL FLUTUANTE DISCRETO NO CANTO DA TELA (Visível em qualquer aba) */}
      {isFloatingVisible && !isFloatingOpen && (
        <div className={classes.floatingPill} onClick={() => setIsFloatingOpen(true)}>
          <div
            className={
              isRunning
                ? isPaused
                  ? classes.pulseDotPaused
                  : classes.pulseDot
                : classes.pulseDotFinished
            }
          />
          <div>
            <Typography variant="caption" style={{ fontWeight: 600, display: "block" }}>
              {isRunning
                ? isPaused
                  ? "⏸️ Disparo Pausado"
                  : `🚀 Disparo: ${stats.sent}/${stats.total} (${progress}%)`
                : `🏁 Disparo Concluído (${stats.sent}/${stats.total})`}
            </Typography>
            {isRunning && countdown > 0 && (
              <Typography variant="caption" style={{ color: "#38bdf8", fontSize: "0.7rem" }}>
                ⏳ Próximo envio em {countdown}s
              </Typography>
            )}
          </div>
          <Tooltip title="Fechar aviso">
            <IconButton
              size="small"
              style={{ color: "#94a3b8", marginLeft: 4 }}
              onClick={(e) => {
                e.stopPropagation();
                dismissFloating();
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      )}

      {/* 2. JANELA FLUTUANTE EXPANDIDA (MONITOR DE ANDAMENTO) */}
      {isFloatingVisible && isFloatingOpen && (
        <Paper elevation={12} className={classes.floatingWindow}>
          <div className={classes.windowHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                className={
                  isRunning
                    ? isPaused
                      ? classes.pulseDotPaused
                      : classes.pulseDot
                    : classes.pulseDotFinished
                }
              />
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                Campanha em Andamento
              </Typography>
              <Chip
                size="small"
                label={isRunning ? (isPaused ? "Pausado" : "Ativo") : "Concluído"}
                style={{
                  height: 20,
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  backgroundColor: isRunning
                    ? isPaused
                      ? "#f59e0b"
                      : "#10b981"
                    : "#38bdf8",
                  color: "#0f172a",
                }}
              />
            </div>
            <div>
              <Tooltip title="Minimizar para o canto">
                <IconButton
                  size="small"
                  style={{ color: "#cbd5e1", marginRight: 4 }}
                  onClick={() => setIsFloatingOpen(false)}
                >
                  <Minimize fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Fechar">
                <IconButton
                  size="small"
                  style={{ color: "#cbd5e1" }}
                  onClick={dismissFloating}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          <div className={classes.windowBody}>
            {/* Barra de Progresso */}
            <div>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="caption" style={{ color: "#94a3b8" }}>
                  Progresso Geral
                </Typography>
                <Typography variant="caption" style={{ fontWeight: 700, color: "#38bdf8" }}>
                  {progress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                className={classes.progressBar}
              />
            </div>

            {/* Quadro de Estatísticas */}
            <div className={classes.statsRow}>
              <div className={classes.statCard}>
                <Typography variant="caption" style={{ color: "#94a3b8", display: "block" }}>
                  Total
                </Typography>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  {stats.total}
                </Typography>
              </div>
              <div className={classes.statCard} style={{ borderColor: "rgba(16, 185, 129, 0.3)" }}>
                <Typography variant="caption" style={{ color: "#10b981", display: "block" }}>
                  Enviados
                </Typography>
                <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#10b981" }}>
                  {stats.sent}
                </Typography>
              </div>
              <div className={classes.statCard} style={{ borderColor: "rgba(239, 68, 68, 0.3)" }}>
                <Typography variant="caption" style={{ color: "#f87171", display: "block" }}>
                  Falhas
                </Typography>
                <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#f87171" }}>
                  {stats.failed}
                </Typography>
              </div>
            </div>

            {/* Status atual do contato / delay */}
            <div
              style={{
                background: "#1e293b",
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #334155",
              }}
            >
              {currentContact ? (
                <Typography variant="caption" style={{ color: "#f8fafc", display: "block" }}>
                  👤 <b>Alvo atual:</b> {currentContact.name ? `${currentContact.name} - ` : ""}
                  {currentContact.number}
                </Typography>
              ) : isRunning ? (
                <Typography variant="caption" style={{ color: "#94a3b8" }}>
                  Iniciando envio...
                </Typography>
              ) : (
                <Typography variant="caption" style={{ color: "#38bdf8" }}>
                  🏁 Campanha finalizada com sucesso!
                </Typography>
              )}

              {isRunning && countdown > 0 && (
                <Typography
                  variant="caption"
                  style={{ color: "#fbbf24", fontWeight: 600, display: "block", marginTop: 2 }}
                >
                  ⏳ Simulando digitação: próximo envio em {countdown}s
                </Typography>
              )}
            </div>

            {/* Terminal de Logs ao Vivo */}
            <div>
              <Typography
                variant="caption"
                style={{ color: "#94a3b8", marginBottom: 4, display: "block" }}
              >
                Logs em Tempo Real:
              </Typography>
              <div className={classes.terminalBox}>
                {logs.length === 0 ? (
                  <span style={{ color: "#64748b" }}>Nenhum log registrado ainda...</span>
                ) : (
                  logs.map((log, idx) => (
                    <div key={idx} style={{ color: log.color, lineHeight: 1.4 }}>
                      <span style={{ color: "#64748b", marginRight: 6 }}>[{log.timestamp}]</span>
                      {log.text}
                    </div>
                  ))
                )}
                <div ref={terminalEndRef} />
              </div>
            </div>

            {/* Botões de Controle */}
            <div className={classes.actionsRow}>
              {isRunning && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={isPaused ? <PlayArrow /> : <Pause />}
                    onClick={togglePause}
                    style={{
                      borderColor: isPaused ? "#10b981" : "#f59e0b",
                      color: isPaused ? "#10b981" : "#f59e0b",
                      fontSize: "0.75rem",
                    }}
                  >
                    {isPaused ? "Retomar" : "Pausar"}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Stop />}
                    onClick={stopBroadcast}
                    style={{ borderColor: "#ef4444", color: "#ef4444", fontSize: "0.75rem" }}
                  >
                    Interromper
                  </Button>
                </>
              )}
              <Button
                size="small"
                variant="contained"
                onClick={() => setIsFloatingOpen(false)}
                style={{
                  backgroundColor: "#334155",
                  color: "#fff",
                  fontSize: "0.75rem",
                  marginLeft: "auto",
                }}
              >
                Minimizar
              </Button>
            </div>
          </div>
        </Paper>
      )}
    </BroadcastContext.Provider>
  );
};

export const useBroadcastContext = () => {
  const context = useContext(BroadcastContext);
  if (!context) {
    throw new Error("useBroadcastContext must be used within a BroadcastProvider");
  }
  return context;
};
